import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import JSZip from "jszip";
import initSqlJs from "sql.js/dist/sql-wasm.js";
import { useTasks } from "../context/TaskContext";
import FlashcardWarmup from "../components/FlashcardWarmup";
import LatexRenderer from "../components/LatexRenderer";
import { getFlashcardParts } from "../utils/flashcardText";
import { TRIG_FLASHCARDS } from "../utils/trigDeck";

const formatTimestamp = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch (error) {
    return String(value);
  }
};

const normalizeTagsInput = (value) =>
  String(value || "")
    .split(",")
    .map((tag) => tag.trim())
    .filter(Boolean);

const renderFlashcardText = (text, keyPrefix) => {
  const segments = [];
  const regex = /<img\s+[^>]*src=["']([^"']+)["'][^>]*>/gi;
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(text || ""))) {
    const before = (text || "").slice(lastIndex, match.index);
    if (before) segments.push({ type: "text", value: before });
    segments.push({ type: "img", src: match[1], alt: "card image" });
    lastIndex = regex.lastIndex;
  }
  const tail = (text || "").slice(lastIndex);
  if (tail) segments.push({ type: "text", value: tail });
  if (!segments.length) {
    segments.push({ type: "text", value: text || "" });
  }

  return segments.flatMap((segment, idx) => {
    if (segment.type === "img") {
      return (
        <div key={`${keyPrefix}-img-${idx}`} className="flashcard-image">
          <img
            src={segment.src}
            alt={segment.alt || "card image"}
            style={{ maxWidth: "100%", maxHeight: "260px" }}
          />
        </div>
      );
    }

    const parts = getFlashcardParts(segment.value || "");
    return parts.map((part, index) => {
      if (part.type === "latex") {
        const className = part.displayMode
          ? "flashcard-math flashcard-math--block"
          : "flashcard-math flashcard-math--inline";
        const Wrapper = part.displayMode ? "div" : "span";
        return (
          <Wrapper key={`${keyPrefix}-latex-${idx}-${index}`} className={className}>
            <LatexRenderer latex={part.value} displayMode={part.displayMode} />
          </Wrapper>
        );
      }
      return (
        <span key={`${keyPrefix}-text-${idx}-${index}`}>{part.value}</span>
      );
    });
  });
};

const Flashcards = () => {
  const location = useLocation();
  const {
    user,
    getFlashcards,
    getDueFlashcards,
    reviewFlashcard,
    createFlashcard,
    updateFlashcard,
    deleteFlashcard,
  } = useTasks();

  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("");
  const [reviewCards, setReviewCards] = useState(null);
  const [editingCard, setEditingCard] = useState(null);
  const [editorDraft, setEditorDraft] = useState({
    deckName: "Obey The Bell",
    noteType: "Basic",
    tags: "",
    front: "",
    back: "",
  });
  const [editorBusy, setEditorBusy] = useState(false);
  const [importBusy, setImportBusy] = useState(false);
  const fileInputRef = useRef(null);
  const autoReviewDue = useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    return params.get("review") === "due";
  }, [location.search]);

  const loadCards = useCallback(async () => {
    if (!user?.uid || !getFlashcards) return;
    setLoading(true);
    setError("");
    try {
      const fetched = await getFlashcards({ limit: 400 });
      setCards(fetched);
    } catch (err) {
      console.error("Failed to load flashcards", err);
      setError(err?.message || "Failed to load flashcards");
    } finally {
      setLoading(false);
    }
  }, [getFlashcards, user?.uid]);

  useEffect(() => {
    void loadCards();
  }, [loadCards, user?.uid]);

  const filteredCards = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (!needle) return cards;
    return cards.filter((card) => {
      const haystack = [
        card.front,
        card.back,
        ...(Array.isArray(card.tags) ? card.tags : []),
        card?.anki?.deckName,
        card?.anki?.noteType,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(needle);
    });
  }, [cards, filter]);

  const openEditorForNew = () => {
    setEditingCard({ id: null });
    setEditorDraft({
      deckName: "Obey The Bell",
      noteType: "Basic",
      tags: "",
      front: "",
      back: "",
    });
  };

  const openEditorForCard = (card) => {
    if (!card) return;
    setEditingCard(card);
    setEditorDraft({
      deckName: String(card?.anki?.deckName || "Obey The Bell"),
      noteType: String(card?.anki?.noteType || "Basic"),
      tags: Array.isArray(card.tags) ? card.tags.join(", ") : "",
      front: String(card.front || ""),
      back: String(card.back || ""),
    });
  };

  const closeEditor = () => {
    setEditingCard(null);
    setEditorBusy(false);
  };

  const handleSaveCard = async () => {
    if (editorBusy) return;
    const front = String(editorDraft.front || "").trim();
    const back = String(editorDraft.back || "").trim();
    if (!front || !back) {
      setError("Front and back are required.");
      return;
    }
    setEditorBusy(true);
    setError("");
    try {
      const tags = normalizeTagsInput(editorDraft.tags);
      const deckName = String(editorDraft.deckName || "Obey The Bell").trim();
      const noteType = String(editorDraft.noteType || "Basic").trim();

      if (!editingCard?.id) {
        await createFlashcard({
          front,
          back,
          tags,
          deckName,
          noteType,
          origin: "manual",
          type: "basic",
        });
      } else {
        await updateFlashcard(editingCard, {
          front,
          back,
          tags,
          deckName,
          noteType,
        });
      }

      closeEditor();
      await loadCards();
    } catch (err) {
      console.error("Failed to save flashcard", err);
      setError(err?.message || "Failed to save flashcard");
    } finally {
      setEditorBusy(false);
    }
  };

  const handleDeleteCard = async (card) => {
    const cardId = card?.id;
    if (!cardId) return;
    // eslint-disable-next-line no-alert
    const confirmed = window.confirm("Delete this flashcard?");
    if (!confirmed) return;
    setError("");
    try {
      await deleteFlashcard(cardId);
      setCards((prev) => prev.filter((entry) => entry.id !== cardId));
    } catch (err) {
      console.error("Failed to delete flashcard", err);
      setError(err?.message || "Failed to delete flashcard");
    }
  };

  const startDueReview = useCallback(async () => {
    setError("");
    try {
      const due = await getDueFlashcards({ limit: 40, minDue: 0 });
      setReviewCards(due);
    } catch (err) {
      console.error("Failed to load due flashcards", err);
      setError(err?.message || "Failed to load due flashcards");
    }
  }, [getDueFlashcards]);

  const stopReview = async () => {
    setReviewCards(null);
    await loadCards();
  };

  const replaceWithTrigDeck = async () => {
    if (loading || editorBusy) return;
    const confirmed = window.confirm(
      "This will delete all existing flashcards and replace them with Calc II trig identities/substitutions. Continue?"
    );
    if (!confirmed) return;
    setLoading(true);
    setError("");
    try {
      const existing = await getFlashcards({ limit: 500 });
      for (const card of existing) {
        if (card?.id) {
          // eslint-disable-next-line no-await-in-loop
          await deleteFlashcard(card.id);
        }
      }
      for (const card of TRIG_FLASHCARDS) {
        // eslint-disable-next-line no-await-in-loop
        await createFlashcard({
          front: card.front,
          back: card.back,
          tags: card.tags || ["trig"],
          deckName: card.deckName || "Calc II – Trig",
          noteType: "Basic",
          origin: "trig-deck",
          type: "basic",
        });
      }
      await loadCards();
    } catch (err) {
      console.error("Failed to replace flashcards with trig deck", err);
      setError(err?.message || "Failed to replace flashcards");
    } finally {
      setLoading(false);
    }
  };

  const importApkgFromBuffer = useCallback(
    async (arrayBuffer) => {
      setImportBusy(true);
      setError("");
      try {
        const zip = await JSZip.loadAsync(arrayBuffer);
        let mediaMap = {};
        try {
          const mediaText = await zip.file("media")?.async("string");
          mediaMap = mediaText ? JSON.parse(mediaText) : {};
        } catch (err) {
          console.warn("Failed to parse media map from .apkg", err);
        }

        const filenameToDataUri = {};
        const guessMime = (name) => {
          if (!name) return "application/octet-stream";
          const lower = name.toLowerCase();
          if (lower.endsWith(".png")) return "image/png";
          if (lower.endsWith(".jpg") || lower.endsWith(".jpeg")) return "image/jpeg";
          if (lower.endsWith(".gif")) return "image/gif";
          if (lower.endsWith(".svg")) return "image/svg+xml";
          return "application/octet-stream";
        };

        const buildDataUriFor = async (filename) => {
          if (!filename) return null;
          if (filenameToDataUri[filename]) return filenameToDataUri[filename];
          const mediaKey = Object.keys(mediaMap).find((key) => mediaMap[key] === filename);
          if (!mediaKey) return null;
          const fileEntry = zip.file(mediaKey);
          if (!fileEntry) return null;
          const base64 = await fileEntry.async("base64");
          const mime = guessMime(filename);
          const dataUri = `data:${mime};base64,${base64}`;
          filenameToDataUri[filename] = dataUri;
          return dataUri;
        };

        const inlineImages = async (html = "") => {
          let result = html;
          const matches = [...html.matchAll(/<img\s+[^>]*src=["']([^"']+)["'][^>]*>/gi)];
          for (const match of matches) {
            const filename = match[1];
            // eslint-disable-next-line no-await-in-loop
            const dataUri = await buildDataUriFor(filename);
            if (dataUri) {
              const escaped = filename.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
              const srcRegex = new RegExp(`(<img[^>]*src=["'])${escaped}(["'][^>]*>)`, "gi");
              result = result.replace(srcRegex, `$1${dataUri}$2`);
            }
          }
          return result;
        };

        const collectionEntry = Object.keys(zip.files).find(
          (name) =>
            name.endsWith(".anki21") ||
            name.endsWith(".anki2") ||
            name.toLowerCase().includes("collection")
        );
        if (!collectionEntry) {
          throw new Error("No collection.anki2 file found in .apkg");
        }
        const dbBytes = await zip.file(collectionEntry).async("uint8array");
        const SQL = await initSqlJs({
          locateFile: (file) => `${process.env.PUBLIC_URL}/${file}`,
        });
        const db = new SQL.Database(dbBytes);

        const colResult = db.exec("SELECT decks FROM col");
        const decksJson = colResult?.[0]?.values?.[0]?.[0] || "{}";
        const decks = JSON.parse(decksJson);

        const cardsResult = db.exec("SELECT nid, did FROM cards");
        const deckByNid = new Map();
        if (cardsResult?.[0]) {
          const { columns, values } = cardsResult[0];
          const idxNid = columns.indexOf("nid");
          const idxDid = columns.indexOf("did");
          values.forEach((row) => {
            const nid = row[idxNid];
            const did = row[idxDid];
            deckByNid.set(nid, did);
          });
        }

        const notesResult = db.exec("SELECT id, flds, tags FROM notes");
        const notes = notesResult?.[0];
        if (!notes) throw new Error("No notes found in .apkg");
        const idxId = notes.columns.indexOf("id");
        const idxFlds = notes.columns.indexOf("flds");
        const idxTags = notes.columns.indexOf("tags");

        for (const row of notes.values) {
          const nid = row[idxId];
          const fields = String(row[idxFlds] || "")
            .split("\u001f")
            .map((f) => f.trim());
          let front = fields[0] || "";
          let back = fields[1] || "";
          front = await inlineImages(front);
          back = await inlineImages(back);
          if (!front || !back) continue;
          const deckId = deckByNid.get(nid);
          const deckName =
            (deckId && decks?.[deckId]?.name) || "Imported Deck";
          const tagsRaw = String(row[idxTags] || "").trim();
          const tags = tagsRaw
            ? tagsRaw
                .split(/\s+/)
                .map((t) => t.replace(/[^-\w]/g, ""))
                .filter(Boolean)
            : [];
          // eslint-disable-next-line no-await-in-loop
          await createFlashcard({
            front,
            back,
            tags,
            deckName,
            noteType: "Basic",
            origin: "apkg-import",
            type: "basic",
          });
        }

        await loadCards();
      } catch (err) {
        console.error("Failed to import .apkg", err);
        setError(err?.message || "Failed to import .apkg");
      } finally {
        setImportBusy(false);
      }
    },
    [createFlashcard, loadCards]
  );

  const handleApkgFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    await importApkgFromBuffer(await file.arrayBuffer());
    event.target.value = "";
  };

  const importBundledApkg = async () => {
    try {
      setImportBusy(true);
      const res = await fetch("/Essential_Calculus_Identities_Integrals_Derivatives_Trig.apkg");
      if (!res.ok) throw new Error("Bundled .apkg not found");
      const buf = await res.arrayBuffer();
      await importApkgFromBuffer(buf);
    } catch (err) {
      console.error("Failed to import bundled .apkg", err);
      setError(err?.message || "Failed to import bundled .apkg");
    } finally {
      setImportBusy(false);
    }
  };

  useEffect(() => {
    if (!autoReviewDue) return;
    if (!user?.uid) return;
    void startDueReview();
  }, [autoReviewDue, startDueReview, user?.uid]);

  return (
    <div
      style={{
        padding: "100px 20px 20px",
        maxWidth: "1100px",
        margin: "0 auto",
      }}
    >
      <div className="page-section__headline">
        <div>
          <h2 style={{ margin: 0 }}>Flashcards</h2>
          <p className="mini-label" style={{ margin: 0 }}>
            Review, edit, and manage cards (stored in an Anki-friendly Basic note shape).
          </p>
        </div>
        <div className="cluster" style={{ gap: "10px" }}>
          <button
            type="button"
            className="btn btn--ghost btn--small"
            onClick={() => void startDueReview()}
            disabled={loading}
          >
            Review due
          </button>
          <button
            type="button"
            className="btn btn--ghost btn--small"
            onClick={() => void replaceWithTrigDeck()}
            disabled={loading}
            title="Replace all flashcards with Calc II trig identities/integrals deck"
          >
            Load trig deck
          </button>
          <button
            type="button"
            className="btn btn--ghost btn--small"
            onClick={() => fileInputRef.current?.click()}
            disabled={loading || importBusy}
          >
            Import .apkg
          </button>
          <button
            type="button"
            className="btn btn--ghost btn--small"
            onClick={() => void importBundledApkg()}
            disabled={loading || importBusy}
          >
            Import bundled .apkg
          </button>
          <button
            type="button"
            className="btn btn--primary btn--small"
            onClick={openEditorForNew}
            disabled={loading}
          >
            New card
          </button>
        </div>
      </div>

      {error ? (
        <p className="mini-label" style={{ color: "#fca5a5" }}>
          {error}
        </p>
      ) : importBusy ? (
        <p className="mini-label" style={{ color: "#cbd5f5" }}>
          Importing…
        </p>
      ) : null}

      {reviewCards ? (
        <div className="card-quiet" style={{ marginTop: "16px" }}>
          <FlashcardWarmup
            cards={reviewCards}
            onRate={reviewFlashcard}
            onComplete={() => void stopReview()}
            showTimer={false}
            title="Review"
            exitLabel="Close"
          />
        </div>
      ) : null}

      <div className="task-form__row" style={{ marginTop: "16px" }}>
        <div className="task-form__field" style={{ maxWidth: "520px" }}>
          <label htmlFor="flashcards-search">Search</label>
          <input
            id="flashcards-search"
            type="text"
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
            placeholder="Search front/back/tags/deck..."
          />
          <input
            ref={fileInputRef}
            type="file"
            accept=".apkg"
            style={{ display: "none" }}
            onChange={(event) => void handleApkgFile(event)}
          />
        </div>
        <div className="cluster" style={{ alignItems: "flex-end", gap: "10px" }}>
          <button
            type="button"
            className="btn btn--ghost btn--small"
            onClick={() => void loadCards()}
            disabled={loading}
          >
            {loading ? "Loading…" : "Refresh"}
          </button>
        </div>
      </div>

      <div style={{ overflowX: "auto" }}>
        <table className="table" style={{ width: "100%", marginTop: "12px" }}>
          <thead>
            <tr>
              <th style={{ textAlign: "left" }}>Due</th>
              <th style={{ textAlign: "left" }}>Deck</th>
              <th style={{ textAlign: "left" }}>Tags</th>
              <th style={{ textAlign: "left" }}>Front</th>
              <th style={{ textAlign: "left" }}>Back</th>
              <th style={{ textAlign: "left" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredCards.map((card) => (
              <tr key={card.id}>
                <td style={{ whiteSpace: "nowrap" }}>
                  {formatTimestamp(card.dueAt)}
                </td>
                <td style={{ whiteSpace: "nowrap" }}>
                  {card?.anki?.deckName || "Obey The Bell"}
                </td>
                <td style={{ whiteSpace: "nowrap" }}>
                  {(Array.isArray(card.tags) ? card.tags : []).join(", ") || "—"}
                </td>
                <td style={{ minWidth: "360px" }}>
                  <div className="mini-label" style={{ margin: 0 }}>
                    {renderFlashcardText(card.front || "", `front-${card.id}`)}
                  </div>
                </td>
                <td style={{ minWidth: "360px" }}>
                  <div className="mini-label" style={{ margin: 0 }}>
                    {renderFlashcardText(card.back || "", `back-${card.id}`)}
                  </div>
                </td>
                <td style={{ whiteSpace: "nowrap" }}>
                  <div className="cluster" style={{ gap: "8px" }}>
                    <button
                      type="button"
                      className="btn btn--ghost btn--small"
                      onClick={() => setReviewCards([card])}
                    >
                      Review
                    </button>
                    <button
                      type="button"
                      className="btn btn--ghost btn--small"
                      onClick={() => openEditorForCard(card)}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="btn btn--ghost btn--small"
                      onClick={() => void handleDeleteCard(card)}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {!filteredCards.length ? (
              <tr>
                <td colSpan={6} className="mini-label" style={{ padding: "18px" }}>
                  {loading ? (
                    "Loading…"
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                      <span>No flashcards found.</span>
                      <div className="cluster" style={{ gap: "10px", flexWrap: "wrap" }}>
                        <button
                          type="button"
                          className="btn btn--ghost btn--small"
                          onClick={() => void replaceWithTrigDeck()}
                          disabled={loading}
                        >
                          Load trig deck
                        </button>
                        <button
                          type="button"
                          className="btn btn--ghost btn--small"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={loading || importBusy}
                        >
                          Import .apkg
                        </button>
                        <button
                          type="button"
                          className="btn btn--ghost btn--small"
                          onClick={() => void importBundledApkg()}
                          disabled={loading || importBusy}
                        >
                          Import bundled .apkg
                        </button>
                        <button
                          type="button"
                          className="btn btn--primary btn--small"
                          onClick={openEditorForNew}
                          disabled={loading}
                        >
                          New card
                        </button>
                      </div>
                    </div>
                  )}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {editingCard ? (
        <div className="modal-overlay" role="dialog" aria-modal="true">
          <div className="modal modal--flashcard">
            <h3 style={{ marginTop: 0 }}>
              {editingCard.id ? "Edit flashcard" : "New flashcard"}
            </h3>
            <div className="task-form__row">
              <div className="task-form__field" style={{ flex: 1 }}>
                <label htmlFor="flashcard-deck">Deck</label>
                <input
                  id="flashcard-deck"
                  type="text"
                  value={editorDraft.deckName}
                  onChange={(e) =>
                    setEditorDraft((prev) => ({ ...prev, deckName: e.target.value }))
                  }
                  placeholder="Obey The Bell"
                />
              </div>
              <div className="task-form__field" style={{ flex: 1 }}>
                <label htmlFor="flashcard-noteType">Note type</label>
                <input
                  id="flashcard-noteType"
                  type="text"
                  value={editorDraft.noteType}
                  onChange={(e) =>
                    setEditorDraft((prev) => ({ ...prev, noteType: e.target.value }))
                  }
                  placeholder="Basic"
                />
              </div>
            </div>
            <div className="task-form__field">
              <label htmlFor="flashcard-tags">Tags (comma-separated)</label>
              <input
                id="flashcard-tags"
                type="text"
                value={editorDraft.tags}
                onChange={(e) =>
                  setEditorDraft((prev) => ({ ...prev, tags: e.target.value }))
                }
                placeholder="integration, trig-sub, ..."
              />
            </div>
            <div className="task-form__field">
              <label htmlFor="flashcard-front">Front</label>
              <textarea
                id="flashcard-front"
                rows={3}
                value={editorDraft.front}
                onChange={(e) =>
                  setEditorDraft((prev) => ({ ...prev, front: e.target.value }))
                }
              />
            </div>
            <div className="task-form__field">
              <label htmlFor="flashcard-back">Back</label>
              <textarea
                id="flashcard-back"
                rows={3}
                value={editorDraft.back}
                onChange={(e) =>
                  setEditorDraft((prev) => ({ ...prev, back: e.target.value }))
                }
              />
            </div>

            <div className="task-form__field">
              <label>Preview</label>
              <div className="card-quiet" style={{ padding: "12px" }}>
                <p className="mini-label" style={{ margin: 0 }}>
                  Front
                </p>
                <div style={{ marginTop: "6px" }}>
                  {renderFlashcardText(editorDraft.front, "preview-front")}
                </div>
                <p className="mini-label" style={{ margin: "12px 0 0" }}>
                  Back
                </p>
                <div style={{ marginTop: "6px" }}>
                  {renderFlashcardText(editorDraft.back, "preview-back")}
                </div>
              </div>
            </div>

            <div className="modal-actions" style={{ justifyContent: "flex-end" }}>
              <button
                type="button"
                className="btn btn--ghost"
                onClick={closeEditor}
                disabled={editorBusy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => void handleSaveCard()}
                disabled={editorBusy}
              >
                {editorBusy ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Flashcards;
